import React, { useMemo } from 'react';
import { Box, Paper, Typography } from '@mui/material';

// 1. Import library functions and our new static JSON data
import { Upset, process } from '@visdesignlab/upset2-react';
import { CoreUpsetData, TableRow, UpsetConfig, DefaultConfig } from '@visdesignlab/upset2-core';
import rawUpsetData from '../data/cysteineData.json';

// 2. Define the schema for the data
const TISSUE_NAMES = ['Breast', 'CNS', 'Colon', 'Leukemia', 'Melanoma', 'NSCL', 'Ovarian', 'Prostate', 'Renal'];
const annotations = {
  columns: {
    'Identifier': 'label' as const,
    'AltIdentifier': 'category' as const,
    ...Object.fromEntries(TISSUE_NAMES.map(name => [name, 'boolean' as const]))
  }
};

export default function AllCysteinesUpsetPlot() {
  // 3. Process data and create config in a useMemo hook. This is fast and synchronous.
  const { processedData, config } = useMemo(() => {
    // Convert imported JSON into the TableRow format for the 'process' function
    const tableRows: TableRow[] = rawUpsetData.map(item => ({ ...item, _key: item.id, _id: item.id, _rev: '' }));
    
    // Create the final CoreUpsetData object
    const data = process(tableRows, annotations.columns);

    // FIX: Configure the plot to be performant by default.
    const newConfig: UpsetConfig = {
      ...DefaultConfig,
      version: '0.1.5',
      
      // Tell the component about ALL possible sets, so they appear in the settings menu.
      allSets: Object.values(data.sets).map(s => ({ name: s.id, size: s.size })),
      
      // Display all 9 sets. Note: This will calculate 2^9=512 intersections, which may impact performance.
      visibleSets: TISSUE_NAMES.map(name => `Set_${name}`),

      // To remove 'AltIdentifier' and 'Deviation', simply remove them from this array.
      visibleAttributes: ['Degree'],
      plots: { scatterplots: [], histograms: [] },
    };
    
    return { processedData: data, config: newConfig };
  }, []);

  // 4. Render the component directly. No loading or error states are needed.
  return (
    <Paper sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h5" gutterBottom>
        All Cysteines Distribution Across Cancer Tissues
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {rawUpsetData.length.toLocaleString()} total cysteines
      </Typography>
      <Box sx={{ flexGrow: 1, height: '800px', width: '100%' }}>
        <Upset
          data={processedData}
          config={config}
          parentHasHeight={true}
        />
      </Box>
    </Paper>
  );
}